/**
 * CurrencyAmountPeriod Component
 * A composite field with currency dropdown, numeric input, and period dropdown
 * 
 * @description Extends CurrencyAmount with a period selector:
 * - Currency dropdown (from Currencies standing data)
 * - Numeric input with 2 decimal places and thousands separators
 * - Period dropdown (Hour, Day, Week, Month, Annum from ConsultancyPeriods)
 * - Parsing of formatted strings like "£1,234 per Hour"
 * - Size variants (xs, sm, md, lg)
 * - Color variants (primary, secondary, accent, etc.)
 * 
 * @example
 * ```typescript
 * const rate = new CurrencyAmountPeriod(container, {
 *   name: 'hourlyRate',
 *   label: 'Hourly Rate',
 *   currencies: [
 *     { CurrencyId: 1, Symbol: '£', Description: 'Pound Sterling' },
 *     { CurrencyId: 2, Symbol: '$', Description: 'US Dollars' }
 *   ],
 *   periods: [
 *     { ConsultancyPeriodId: 1, Name: 'Hour' },
 *     { ConsultancyPeriodId: 2, Name: 'Day' },
 *     { ConsultancyPeriodId: 5, Name: 'Annum' }
 *   ],
 *   currencyId: 1,
 *   amount: 50,
 *   periodId: 1,
 *   size: 'sm'
 * });
 * await rate.initialize();
 * ```
 */

import { IComponent } from './IComponent';
import { Currency } from './CurrencyAmount';
import { Logger } from '@/services/Logger';

const log = Logger.scope('CurrencyAmountPeriod');

/**
 * Period data structure from cached standing data
 */
export interface ConsultancyPeriod {
  ConsultancyPeriodId: number;
  Name: string;
}

/**
 * Value returned by CurrencyAmountPeriod component
 */
export interface CurrencyAmountPeriodValue {
  /** The currency ID */
  currencyId: number;
  /** The currency symbol */
  symbol: string;
  /** The numeric amount */
  amount: number;
  /** The period ID */
  periodId: number;
  /** The period name */
  periodName: string;
  /** Formatted display string e.g. "£1,234.56 per Hour" */
  formatted: string;
}

/**
 * Configuration options for CurrencyAmountPeriod component
 */
export interface CurrencyAmountPeriodOptions {
  /** Unique name for the field */
  name: string;
  /** Optional label displayed above the field */
  label?: string;
  /** Array of currencies from standing data */
  currencies: Currency[];
  /** Array of periods from standing data */
  periods: ConsultancyPeriod[];
  /** Currently selected currency ID */
  currencyId?: number;
  /** Initial amount value */
  amount?: number;
  /** Currently selected period ID */
  periodId?: number;
  /** Size variant */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** Color variant */
  color?: 'primary' | 'secondary' | 'accent' | 'info' | 'success' | 'warning' | 'error';
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is required */
  required?: boolean;
  /** Whether the field is readonly */
  readonly?: boolean;
  /** Placeholder for amount input */
  placeholder?: string;
  /** Number of decimal places (default 2) */
  decimals?: number;
  /** Minimum amount */
  min?: number;
  /** Maximum amount */
  max?: number;
  /** CSS classes to add to the container */
  containerClass?: string;
  /** Callback when value changes */
  onChange?: (value: CurrencyAmountPeriodValue) => void;
}

/**
 * CurrencyAmountPeriod Component Class
 * Implements a composite currency + amount + period input
 */
export class CurrencyAmountPeriod implements IComponent {
  private container: HTMLElement;
  private options: CurrencyAmountPeriodOptions;
  private currencySelect: HTMLSelectElement | null = null;
  private amountInput: HTMLInputElement | null = null;
  private periodSelect: HTMLSelectElement | null = null;
  private initialized = false;

  constructor(container: HTMLElement, options: CurrencyAmountPeriodOptions) {
    this.container = container;
    this.options = {
      size: 'sm',
      decimals: 2,
      currencyId: options.currencies[0]?.CurrencyId,
      periodId: options.periods[0]?.ConsultancyPeriodId,
      amount: 0,
      ...options
    };
  }

  /**
   * Initialize the component
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;

    this.render();
    this.bindEvents();
    this.initialized = true;

    log.debug('CurrencyAmountPeriod initialized', {
      name: this.options.name,
      currencyId: this.options.currencyId,
      amount: this.options.amount,
      periodId: this.options.periodId
    });
  }

  /**
   * Render the component
   */
  private render(): void {
    const {
      name,
      label,
      currencies,
      periods,
      currencyId,
      amount,
      periodId,
      size,
      color,
      disabled,
      required,
      readonly: isReadonly,
      placeholder,
      containerClass
    } = this.options;

    // Build size class
    const sizeClass = size ? `select-${size}` : 'select-sm';
    const inputSizeClass = size ? `input-${size}` : 'input-sm';
    
    // Build color class
    const colorClass = color ? `select-${color}` : '';
    const inputColorClass = color ? `input-${color}` : '';

    // Build currency options HTML
    const currencyOptionsHtml = currencies
      .sort((a, b) => (a.Ordering ?? 999) - (b.Ordering ?? 999))
      .map(c => `<option value="${c.CurrencyId}" ${c.CurrencyId === currencyId ? 'selected' : ''}>${c.Symbol}</option>`)
      .join('');

    // Build period options HTML
    const periodOptionsHtml = periods
      .map(p => `<option value="${p.ConsultancyPeriodId}" ${p.ConsultancyPeriodId === periodId ? 'selected' : ''}>${p.Name}</option>`)
      .join('');

    // Format initial amount
    const formattedAmount = this.formatNumber(amount ?? 0);

    // Build the HTML
    const html = `
      <div class="currency-amount-period-container ${containerClass ?? ''}">
        ${label ? `<label class="label"><span class="label-text">${label}${required ? ' <span class="text-error">*</span>' : ''}</span></label>` : ''}
        <div class="join w-full">
          <select 
            name="${name}_currency" 
            class="select select-bordered ${sizeClass} ${colorClass} join-item w-20 min-w-20"
            ${disabled ? 'disabled' : ''}
            ${isReadonly ? 'disabled' : ''}
          >
            ${currencyOptionsHtml}
          </select>
          <input 
            type="text" 
            name="${name}_amount"
            class="input input-bordered ${inputSizeClass} ${inputColorClass} join-item flex-1 text-right font-mono"
            value="${formattedAmount}"
            placeholder="${placeholder ?? '0.00'}"
            inputmode="decimal"
            ${disabled ? 'disabled' : ''}
            ${isReadonly ? 'readonly' : ''}
          />
          <span class="join-item flex items-center px-2 bg-base-100 border border-base-content/20 text-sm text-base-content">per</span>
          <select 
            name="${name}_period" 
            class="select select-bordered ${sizeClass} ${colorClass} join-item w-24 min-w-24"
            ${disabled ? 'disabled' : ''}
            ${isReadonly ? 'disabled' : ''}
          >
            ${periodOptionsHtml}
          </select>
        </div>
      </div>
    `;

    this.container.innerHTML = html;

    // Get references
    this.currencySelect = this.container.querySelector(`select[name="${name}_currency"]`);
    this.amountInput = this.container.querySelector(`input[name="${name}_amount"]`);
    this.periodSelect = this.container.querySelector(`select[name="${name}_period"]`);
  }

  /**
   * Bind event listeners
   */
  private bindEvents(): void {
    // Currency change
    this.currencySelect?.addEventListener('change', () => {
      this.notifyChange();
    });

    // Period change
    this.periodSelect?.addEventListener('change', () => {
      this.notifyChange();
    });

    // Amount input - format on blur
    this.amountInput?.addEventListener('blur', () => {
      const rawValue = this.parseNumber(this.amountInput!.value);
      this.amountInput!.value = this.formatNumber(rawValue);
      this.notifyChange();
    });

    // Allow only numeric input
    this.amountInput?.addEventListener('keydown', (e) => {
      const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
      
      if (allowedKeys.includes(e.key)) return;
      
      // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      if (e.ctrlKey && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) return;
      
      // Allow numbers, period, comma, minus
      if (/[0-9.,\-]/.test(e.key)) return;
      
      e.preventDefault();
    });

    // Format on paste
    this.amountInput?.addEventListener('paste', (e) => {
      e.preventDefault();
      const pastedText = e.clipboardData?.getData('text') ?? '';
      const numericValue = this.parseNumber(pastedText);
      this.amountInput!.value = this.formatNumber(numericValue);
      this.notifyChange();
    });
  }

  /**
   * Format a number with thousands separators and decimals
   */
  private formatNumber(value: number): string {
    const decimals = this.options.decimals ?? 2;
    return value.toLocaleString('en-GB', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  }

  /**
   * Parse a formatted number string to a number
   */
  private parseNumber(text: string): number {
    if (!text || text.trim() === '') return 0;
    
    // Remove currency symbols and whitespace
    let cleaned = text.replace(/[£$€¥₹₩₪]/g, '').trim();
    
    // Remove thousands separators (commas)
    cleaned = cleaned.replace(/,/g, '');
    
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  }

  /**
   * Parse a formatted string like "£1,234 per Hour" into components
   */
  static parse(text: string, currencies: Currency[], periods: ConsultancyPeriod[]): CurrencyAmountPeriodValue | null {
    // Defensive: ensure text is a string
    if (!text || typeof text !== 'string') return null;
    if (text.trim() === '') return null;

    let remaining = text.trim();
    
    // Check for semicolon-separated format from API: "CurrencySymbol;Amount;PeriodName"
    // Example: "€;307075.00;Annum" or "£;50000;Hour"
    if (remaining.includes(';')) {
      const parts = remaining.split(';');
      if (parts.length >= 3) {
        const symbol = parts[0];
        const amountStr = parts[1];
        const periodName = parts[2];
        
        const amount = parseFloat(amountStr.replace(/,/g, ''));
        if (isNaN(amount)) return null;
        
        // Find the currency by symbol
        const currency = currencies.find(c => c.Symbol === symbol) ?? currencies[0];
        // Find the period by name (case-insensitive)
        const period = periods.find(p => p.Name.toLowerCase() === periodName.toLowerCase()) ?? periods[0];
        
        return {
          currencyId: currency?.CurrencyId ?? 0,
          symbol: currency?.Symbol ?? '',
          amount,
          periodId: period?.ConsultancyPeriodId ?? 0,
          periodName: period?.Name ?? '',
          formatted: `${currency?.Symbol ?? ''}${amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} per ${period?.Name ?? ''}`
        };
      }
    }
    
    // Try to extract currency symbol from the beginning
    let symbol = '';
    for (const currency of currencies) {
      if (remaining.startsWith(currency.Symbol)) {
        symbol = currency.Symbol;
        remaining = remaining.substring(currency.Symbol.length).trim();
        break;
      }
    }

    // Try to extract period from the end (look for "per X" pattern)
    let periodName = '';
    const perMatch = remaining.match(/\s+per\s+(\w+)$/i);
    if (perMatch) {
      const matchedPeriod = perMatch[1];
      const period = periods.find(p => p.Name.toLowerCase() === matchedPeriod.toLowerCase());
      if (period) {
        periodName = period.Name;
        remaining = remaining.replace(perMatch[0], '').trim();
      }
    }

    // Parse the amount
    const cleaned = remaining.replace(/,/g, '');
    const amount = parseFloat(cleaned);
    
    if (isNaN(amount)) return null;

    // Find the currency
    const currency = currencies.find(c => c.Symbol === symbol) ?? currencies[0];
    const period = periods.find(p => p.Name === periodName) ?? periods[0];
    
    return {
      currencyId: currency?.CurrencyId ?? 0,
      symbol: currency?.Symbol ?? '',
      amount,
      periodId: period?.ConsultancyPeriodId ?? 0,
      periodName: period?.Name ?? '',
      formatted: `${currency?.Symbol ?? ''}${amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} per ${period?.Name ?? ''}`
    };
  }

  /**
   * Notify onChange callback
   */
  private notifyChange(): void {
    if (this.options.onChange) {
      this.options.onChange(this.getValue());
    }
  }

  /**
   * Get the current value
   */
  getValue(): CurrencyAmountPeriodValue {
    const currencyId = parseInt(this.currencySelect?.value ?? '0', 10);
    const currency = this.options.currencies.find(c => c.CurrencyId === currencyId);
    const amount = this.parseNumber(this.amountInput?.value ?? '0');
    const periodId = parseInt(this.periodSelect?.value ?? '0', 10);
    const period = this.options.periods.find(p => p.ConsultancyPeriodId === periodId);
    const symbol = currency?.Symbol ?? '';
    const periodName = period?.Name ?? '';

    return {
      currencyId,
      symbol,
      amount,
      periodId,
      periodName,
      formatted: `${symbol}${this.formatNumber(amount)} per ${periodName}`
    };
  }

  /**
   * Get the value in API format for saving: "CurrencySymbol Amount per PeriodName"
   * Example: "€50000 per Annum" or "£25.50 per Hour"
   * Note: The API stores this as a record ID but expects human-readable format when saving
   */
  getApiValue(): string {
    const value = this.getValue();
    return `${value.symbol}${value.amount} per ${value.periodName}`;
  }

  /**
   * Set the value
   */
  setValue(currencyId: number, amount: number, periodId: number): void {
    if (this.currencySelect) {
      this.currencySelect.value = currencyId.toString();
    }
    if (this.amountInput) {
      this.amountInput.value = this.formatNumber(amount);
    }
    if (this.periodSelect) {
      this.periodSelect.value = periodId.toString();
    }
  }

  /**
   * Set value from a formatted string like "£1,234 per Hour"
   */
  setValueFromString(text: string): void {
    const parsed = CurrencyAmountPeriod.parse(text, this.options.currencies, this.options.periods);
    if (parsed) {
      this.setValue(parsed.currencyId, parsed.amount, parsed.periodId);
    }
  }

  /**
   * Get the amount only
   */
  getAmount(): number {
    return this.parseNumber(this.amountInput?.value ?? '0');
  }

  /**
   * Set the amount only
   */
  setAmount(amount: number): void {
    if (this.amountInput) {
      this.amountInput.value = this.formatNumber(amount);
    }
  }

  /**
   * Get the selected currency
   */
  getCurrency(): Currency | undefined {
    const currencyId = parseInt(this.currencySelect?.value ?? '0', 10);
    return this.options.currencies.find(c => c.CurrencyId === currencyId);
  }

  /**
   * Set the currency by ID
   */
  setCurrency(currencyId: number): void {
    if (this.currencySelect) {
      this.currencySelect.value = currencyId.toString();
    }
  }

  /**
   * Get the selected period
   */
  getPeriod(): ConsultancyPeriod | undefined {
    const periodId = parseInt(this.periodSelect?.value ?? '0', 10);
    return this.options.periods.find(p => p.ConsultancyPeriodId === periodId);
  }

  /**
   * Set the period by ID
   */
  setPeriod(periodId: number): void {
    if (this.periodSelect) {
      this.periodSelect.value = periodId.toString();
    }
  }

  /**
   * Enable the component
   */
  enable(): void {
    if (this.currencySelect) this.currencySelect.disabled = false;
    if (this.amountInput) this.amountInput.disabled = false;
    if (this.periodSelect) this.periodSelect.disabled = false;
  }

  /**
   * Disable the component
   */
  disable(): void {
    if (this.currencySelect) this.currencySelect.disabled = true;
    if (this.amountInput) this.amountInput.disabled = true;
    if (this.periodSelect) this.periodSelect.disabled = true;
  }

  /**
   * Check if initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Destroy the component
   */
  destroy(): void {
    this.container.innerHTML = '';
    this.currencySelect = null;
    this.amountInput = null;
    this.periodSelect = null;
    this.initialized = false;
  }
}
